import { useState } from 'react';
import { Camera, Download, Sparkles, X, History } from 'lucide-react';
import { HudButton } from '../hud';
import { resolveThumbUrl } from '../../api/materialthequeApi';

/** RoomRenderDialog — options avant de générer le rendu d'une pièce. */
export const RoomRenderDialog = ({ section, hasSourcePhoto, onClose, onConfirm, busy }) => {
  const [keepView, setKeepView] = useState(hasSourcePhoto);
  const [instruction, setInstruction] = useState('');
  return (
    <>
      <div className="fixed inset-0 bg-black/65 backdrop-blur-sm z-[1400]" onClick={busy ? undefined : onClose} />
      <div role="dialog" aria-modal="true" data-testid="room-render-dialog"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[520px] rounded-2xl z-[1410]"
        style={{ background: 'linear-gradient(to bottom, #0F0F12, #09090B)', border: '1px solid var(--c-border)', boxShadow: 'var(--shadow-cockpit)' }}>
        <div className="px-6 py-5 flex items-start justify-between" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Rendu 3D · {section.label}</span>
            <h3 className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}>Générer la pièce</h3>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--c-text-muted)' }}>
              {section.items.length} matériau{section.items.length > 1 ? 'x' : ''} de la section seront appliqués, vignettes injectées comme références.
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0" data-testid="room-render-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <label className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer ${hasSourcePhoto ? '' : 'opacity-50 cursor-not-allowed'}`} style={{ border: '1px solid var(--c-border)', background: keepView && hasSourcePhoto ? 'rgba(217,160,91,0.06)' : 'transparent' }}>
            <input type="checkbox" checked={keepView && hasSourcePhoto} disabled={!hasSourcePhoto} onChange={(e) => setKeepView(e.target.checked)} className="mt-0.5 accent-[#D9A05B]" data-testid="room-render-keep-view" />
            <span>
              <span className="block text-[13.5px] font-medium" style={{ color: 'var(--c-text)' }}>Garder la vue de la photo source</span>
              <span className="block text-[12px] mt-0.5" style={{ color: 'var(--c-text-muted)' }}>
                {hasSourcePhoto ? 'Même cadrage et géométrie, seuls les matériaux sont appliqués.' : 'Aucune photo source pour cette pièce — composition libre.'}
              </span>
            </span>
          </label>
          <label className="block">
            <span className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>Consigne (optionnelle)</span>
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={600} rows={3}
              placeholder="Ex : vue depuis l'entrée, lumière du soir, ambiance chaleureuse…"
              className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none resize-none"
              style={{ background: 'var(--c-surface-1)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
              data-testid="room-render-instruction" />
          </label>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--c-border)' }}>
          <HudButton variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="room-render-cancel">Annuler</HudButton>
          <HudButton variant="primary" size="md" onClick={() => onConfirm({ keep_source_view: keepView && hasSourcePhoto, instruction: instruction.trim() || null })} disabled={busy} leftIcon={<Sparkles className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />} data-testid="room-render-confirm">
            {busy ? 'Génération…' : 'Générer le rendu'}
          </HudButton>
        </div>
      </div>
    </>
  );
};

/** SectionRendersStrip — dernier rendu en grand + historique (3 max). */
export const SectionRendersStrip = ({ sectionId, renders }) => {
  const [activeId, setActiveId] = useState(null);
  if (!renders?.length) return null;
  const active = renders.find((r) => r.render_id === activeId) || renders[0];
  const url = `${resolveThumbUrl(active.url)}?t=${encodeURIComponent(active.generated_at)}`;
  return (
    <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--c-border)', background: 'rgba(217,160,91,0.03)' }} data-testid={`section-renders-${sectionId}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <span className="hud-eyebrow hud-eyebrow-gold inline-flex items-center gap-1.5"><Camera className="w-3 h-3" /> Rendu de la pièce</span>
        <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
          {active.keep_source_view && <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)' }}>Vue source conservée</span>}
          {active.instruction && <span className="px-1.5 py-0.5 rounded truncate max-w-[260px]" style={{ background: 'rgba(255,255,255,0.05)' }} title={active.instruction}>« {active.instruction} »</span>}
          <span className="hud-tabular">{new Date(active.generated_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
          <a href={url} download={`${sectionId}-${active.render_id}.${active.mime_type?.includes('jpe') ? 'jpg' : 'png'}`} className="hud-btn hud-btn-secondary hud-btn-sm" data-testid={`section-render-download-${sectionId}`}>
            <Download className="w-3.5 h-3.5" /> Télécharger
          </a>
        </div>
      </div>
      <div className="flex gap-3 items-start">
        <a href={url} target="_blank" rel="noreferrer" className="block flex-1 rounded-xl overflow-hidden" style={{ border: '1px solid var(--c-border-accent)', maxWidth: 720 }}>
          <img src={url} alt={`Rendu ${sectionId}`} className="w-full h-auto object-cover" style={{ maxHeight: 400 }} data-testid={`section-render-image-${sectionId}`} />
        </a>
        {renders.length > 1 && (
          <div className="flex flex-col gap-2 w-[110px] shrink-0">
            <span className="hud-eyebrow inline-flex items-center gap-1" style={{ color: 'var(--c-text-muted)' }}><History className="w-3 h-3" /> Historique</span>
            {renders.map((r) => (
              <button key={r.render_id} type="button" onClick={() => setActiveId(r.render_id)} className="rounded-lg overflow-hidden aspect-video" data-testid={`section-render-history-${r.render_id}`}
                style={{ border: `1px solid ${r.render_id === active.render_id ? 'var(--c-gold)' : 'var(--c-border)'}`, opacity: r.render_id === active.render_id ? 1 : 0.7 }}>
                <img src={`${resolveThumbUrl(r.url)}?t=${encodeURIComponent(r.generated_at)}`} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
